export type SddStageKey = "specify" | "plan" | "task_breakdown" | "implement" | "validate";
export type SddSpecFormat = "yaml" | "json";
export type SddValidationStatus = "draft" | "validating" | "compliant" | "errors";
export type SddHierarchyLevel = "product" | "feature" | "story";

export interface SddRequiredWorkProduct {
    name: string;
    level: SddHierarchyLevel;
    stage: SddStageKey;
    /** Stable ai.workproduct.variable identifier. Prefer this over the editable display title. */
    variable?: string;
    /** Deployed legacy variables accepted while catalog migrations converge on the canonical variable. */
    variableAliases?: readonly string[];
    /** Historical display titles accepted while older environments are upgraded. */
    aliases?: readonly string[];
}

export interface SddWorkProductSource {
    name: string;
    description?: string;
    shortdescription?: string;
    variable?: string;
    level?: string;
    display_level?: readonly string[];
    scope_level?: "PRODUCT" | "FEATURE" | "USERSTORY";
    hasContent?: boolean;
    state?: string;
    approved?: boolean;
}

export interface SddStageDefinition {
    key: SddStageKey;
    number: number;
    title: string;
    subtitle: string;
    phase: "discovery" | "design" | "task-breakdown" | "code" | "qa";
    items: string[];
}

export interface SddValidationIssue {
    line: number;
    column: number;
    message: string;
    severity: "error" | "warning";
}

export function getSddWorkProductDescription(source?: Pick<SddWorkProductSource, "shortdescription" | "description">): string {
    return source?.shortdescription?.trim() || source?.description?.trim() || "No description is available for this work product.";
}

export const SDD_STAGES: SddStageDefinition[] = [
    {
        key: "specify",
        number: 1,
        title: "Specify",
        subtitle: "Frame the problem before writing code.",
        phase: "discovery",
        items: ["Problem statement", "Users", "Goals", "Non-goals", "Acceptance criteria", "Unclear questions"]
    },
    {
        key: "plan",
        number: 2,
        title: "Plan",
        subtitle: "Decide the shape of the solution.",
        phase: "design",
        items: ["Architecture decisions", "Affected modules", "Data model", "API contracts", "Migrations", "Security concerns", "Test strategy"]
    },
    {
        key: "task_breakdown",
        number: 3,
        title: "Task Breakdown",
        subtitle: "Turn the plan into small, ordered steps.",
        phase: "task-breakdown",
        items: ["Small implementation tasks", "Dependencies", "Files likely to change", "Validation per task", "Human review checkpoints"]
    },
    {
        key: "implement",
        number: 4,
        title: "Implement",
        subtitle: "Execute one task at a time.",
        phase: "code",
        items: ["One task at a time", "Context reset between tasks", "Apply constraints", "Update plan when reality differs"]
    },
    {
        key: "validate",
        number: 5,
        title: "Validate",
        subtitle: "Confirm the work meets the specification.",
        phase: "qa",
        items: ["Tests", "Lint", "Type checks", "Acceptance criteria", "Manual review", "Spec-to-code diff"]
    }
];

type RequiredWorkProductInput = string | {
    name: string;
    variable?: string;
    variableAliases?: readonly string[];
    aliases?: readonly string[];
};

const required = (stage: SddStageKey, level: SddHierarchyLevel, inputs: RequiredWorkProductInput[]): SddRequiredWorkProduct[] =>
    inputs.map(input => ({ ...(typeof input === "string" ? { name: input } : input), level, stage }));

/** KAN-1696 catalog, aligned with the scoped ai.workproduct records added by the SDD backend scripts. */
export const SDD_REQUIRED_WORK_PRODUCTS: Record<SddStageKey, SddRequiredWorkProduct[]> = {
    specify: [
        ...required("specify", "product", [
            { name: "Constitution", variable: "constitution", aliases: ["SDD Project Constitution"] },
            { name: "Personas", variable: "marketing-personas" },
            {
                name: "Product Requirements Definition- Prod",
                variable: "product-requirements-definition-product-hierarchy",
                aliases: ["Product Requirements Spec - Prod", "Product Requirements Definition"]
            },
            {
                name: "Software Requirements Specification",
                variable: "software-requirements-specification-product",
                aliases: ["Software Requirements Specification (Product)"]
            },
            {
                name: "Specification Document - Product",
                variable: "specification-document-product",
                aliases: ["Specification Document (Product)"]
            },
            {
                name: "Ubiquitous Language Dictionary",
                variable: "ubiquitous-language-dictionary-product",
                variableAliases: ["ubiquitous-language-dictionary"]
            }
        ]),
        ...required("specify", "feature", [
            {
                name: "Product Requirements Definition- Feature",
                variable: "product-requirements-definition",
                aliases: ["Product Requirements Spec - Feature", "Product Requirements Definition - Feature"]
            },
            {
                name: "Specification Document - Feature",
                variable: "specification-document-feature",
                aliases: ["Specification Document (Feature)"]
            }
        ]),
        ...required("specify", "story", [
            { name: "Acceptance Criteria", variable: "acceptance-criteria-user-story" },
            { name: "Interface Contract Specifications", variable: "interface-contract-specifications-user-story" },
            {
                name: "Specification Document - User Story",
                variable: "specification-document-user-story",
                aliases: ["Specification Document (User Story)"]
            }
        ])
    ],
    plan: [
        ...required("plan", "product", [
            {
                name: "API & Interface Contracts Design - Product",
                variable: "api-contracts-design-product",
                variableAliases: ["api-interface-contracts-design-product"],
                aliases: ["API Contracts Design - Product", "API & Interface Contracts Design (Product)"]
            },
            {
                name: "Architecture Plan & Design - Product",
                variable: "high-level-technical-design-document",
                aliases: [
                    "Architecture Plan and Design - Product",
                    "SDD Architecture Plan & Design_PRODUCT",
                    "SDD Architecture Plan and Design PRODUCT",
                    "SDD Architecture Plan and Design - Product"
                ]
            },
            { name: "Data Model - Product", variable: "data-model-product" },
            {
                name: "Security Implementation - Product",
                variable: "security-implementation-product",
                aliases: ["Security Implementation Plan - Product", "Security Implementation Plan (Product)"]
            },
            {
                name: "Test Plan Matrix",
                variable: "test-plan",
                variableAliases: ["test-plan-matrix-specification-product"],
                aliases: ["Test Plan Matrix Specification"]
            }
        ]),
        ...required("plan", "feature", [
            {
                name: "API & Interface Contracts Design - Feature",
                variable: "api-contracts-design-feature",
                variableAliases: ["api-interface-contracts-design-feature"],
                aliases: ["API Contracts Design - Feature", "API & Interface Contracts Design (Feature)"]
            },
            {
                name: "Architecture Plan & Design - Feature",
                variable: "feature-high-level-req",
                aliases: [
                    "Architecture Plan and Design - Feature",
                    "High Level Technical Design - Feature",
                    "SDD Architecture Plan & Design_FEATURE",
                    "SDD Architecture Plan and Design FEATURE",
                    "SDD Architecture Plan and Design - Feature"
                ]
            },
            { name: "Data Model - Feature", variable: "data-model-feature" },
            { name: "Security Implementation - Feature", variable: "security-architecture-document" }
        ]),
        ...required("plan", "story", [
            {
                name: "API & Interface Contracts Design - User Story",
                variable: "api-contracts-design-user-story",
                variableAliases: ["api-interface-contracts-design-user-story"],
                aliases: ["API Contracts Design - User Story", "API & Interface Contracts Design (User Story)"]
            },
            {
                name: "Architecture Plan & Design - User Story",
                variable: "architecture-plan-and-design-user-story",
                aliases: [
                    "Architecture Plan and Design - User Story",
                    "SDD Architecture Plan & Design_USERSTORY",
                    "SDD Architecture Plan and Design USERSTORY",
                    "SDD Architecture Plan and Design - User Story"
                ]
            },
            { name: "Data Model - User Story", variable: "data-model-user-story" },
            {
                name: "Dependencies on Pre-Existing and Future Modules",
                variable: "dependencies-pre-existing-future-modules-user-story",
                variableAliases: ["dependencies-preexisting-future-modules-user-story"]
            },
            { name: "Security Implementation - User Story", variable: "security-implementation-user-story" }
        ])
    ],
    task_breakdown: required("task_breakdown", "story", [
        { name: "Dependency Graph", variable: "dependency-graph-user-story" },
        { name: "User Story Ticket", variable: "detailed-requirements", aliases: ["User Story Tickets"] },
        { name: "User Story-to-Spec Mapping", variable: "user-story-to-spec-mapping-user-story" }
    ]),
    implement: required("implement", "story", [
        {
            name: "Code Documentation",
            variable: "code-documentation",
            aliases: [".MD File Code Generation", "MD File Code Generation"]
        },
        {
            name: "Production Source Code",
            variable: "userstory_code",
            aliases: ["Code Generation"]
        },
        {
            name: "Specification-First / Test-Driven Development (TDD) Scripts",
            variable: "specification-first-tdd-scripts-user-story"
        }
    ]),
    validate: required("validate", "story", [
        { name: "Spec to Code Diff", variable: "spec-to-code-diff-user-story" },
        { name: "Test Execution & Coverage Reports", variable: "test-execution-coverage-reports-user-story" },
        { name: "Validation Report", variable: "validation-report-user-story" }
    ])
};

export const ALL_SDD_REQUIRED_WORK_PRODUCTS = Object.values(SDD_REQUIRED_WORK_PRODUCTS).flat();

export function normalizeWorkProductName(value: string): string {
    return value
        .toLowerCase()
        .replace(/\bprod\b/g, "product")
        .replace(/\bquestionaires\b/g, "questionnaires")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

/** Recognize a direct generation command for a required SDD work product.
 * Matching is deliberately case-insensitive so intent routing cannot depend on
 * the capitalization used in the catalog label. */
export function resolveWorkProductGenerationName(message: string, availableNames: readonly string[] = []): string | null {
    const match = message.trim().match(/^(?:please\s+)?(generate|create|build)\s+(.+?)[.!?]*$/i);
    if (!match) return null;
    const verb = match[1].toLowerCase();
    const requestedText = match[2].replace(/[*_`]/g, "").trim();
    const requestedName = normalizeWorkProductName(requestedText);
    const definition = ALL_SDD_REQUIRED_WORK_PRODUCTS.find(candidate =>
        [candidate.name, ...(candidate.aliases || [])]
            .map(normalizeWorkProductName)
            .includes(requestedName)
    );
    if (definition) return definition.name;

    const visibleName = availableNames.find(name => normalizeWorkProductName(name) === requestedName);
    if (visibleName) return visibleName;

    const genericBuildTarget = /^(?:a |the )?(?:new )?(?:product|application|app)$/i.test(requestedText);
    if (genericBuildTarget) return null;

    // This helper is used only while Feature Studio is active. Preserve the
    // requested title for optional/server-defined catalog entries that are not
    // present in the static required-work-product list; the host resolver will
    // canonicalize it against the loaded catalog before submitting a job.
    return requestedName && ["generate", "create", "build"].includes(verb) ? requestedText : null;
}

function sourceSupportsLevel(source: SddWorkProductSource, level: SddHierarchyLevel): boolean {
    const expected = level === "story" ? "userstory" : level;
    if (source.scope_level) {
        return source.scope_level.toLowerCase().replace(/[^a-z]/g, "") === expected;
    }
    const declared = [source.level, ...(source.display_level || [])].filter(Boolean).map(value =>
        String(value)
            .toLowerCase()
            .replace(/[^a-z]/g, "")
    );
    return declared.length === 0 || declared.includes(expected);
}

export function findRequiredWorkProductSource<T extends SddWorkProductSource>(definition: SddRequiredWorkProduct, sources: readonly T[]): T | undefined {
    const acceptedVariables = [definition.variable, ...(definition.variableAliases || [])]
        .filter((value): value is string => Boolean(value))
        .map(value => value.trim().toLowerCase());
    if (acceptedVariables.length > 0) {
        const variableMatch = sources.find(
            source => Boolean(source.variable) &&
                acceptedVariables.includes(String(source.variable).trim().toLowerCase()) &&
                sourceSupportsLevel(source, definition.level)
        );
        if (variableMatch) return variableMatch;
    }

    const acceptedNames = [definition.name, ...(definition.aliases || [])].map(normalizeWorkProductName);
    return sources.find(source => acceptedNames.includes(normalizeWorkProductName(source.name)) && sourceSupportsLevel(source, definition.level));
}

/** Resolve an FDE generation request to a canonical work-product source. */
export function findWorkProductGenerationSource<T extends SddWorkProductSource & { id: string | number }>(
    sources: readonly T[],
    request: { workProductId?: string; workProductName?: string }
): T | undefined {
    const resolveByName = (workProductName?: string): T | undefined => {
        const nameNeedle = normalizeWorkProductName(workProductName || "");
        if (!nameNeedle) return undefined;

        const exact = sources.find(source => normalizeWorkProductName(source.name) === nameNeedle);
        if (exact) return exact;

        const definition = ALL_SDD_REQUIRED_WORK_PRODUCTS.find(candidate =>
            [candidate.name, ...(candidate.aliases || [])]
                .map(normalizeWorkProductName)
                .includes(nameNeedle)
        );
        if (definition) {
            const canonical = findRequiredWorkProductSource(definition, sources);
            if (canonical) return canonical;
        }

        const startsWith = sources.filter(source => normalizeWorkProductName(source.name).startsWith(nameNeedle));
        if (startsWith.length === 1) return startsWith[0];

        const contains = sources.filter(source => normalizeWorkProductName(source.name).includes(nameNeedle));
        return contains.length === 1 ? contains[0] : undefined;
    };

    const strictNameNeedle = normalizeWorkProductName(request.workProductName || "");
    if (strictNameNeedle) {
        const strictNameMatch = sources.find(source => normalizeWorkProductName(source.name) === strictNameNeedle);
        if (strictNameMatch) return strictNameMatch;

        const strictDefinition = ALL_SDD_REQUIRED_WORK_PRODUCTS.find(candidate =>
            [candidate.name, ...(candidate.aliases || [])]
                .map(normalizeWorkProductName)
                .includes(strictNameNeedle)
        );
        if (strictDefinition) {
            const strictCanonical = findRequiredWorkProductSource(strictDefinition, sources);
            if (strictCanonical) return strictCanonical;
        }
    }

    const idNeedle = String(request.workProductId || "").trim().toLowerCase();
    if (idNeedle) {
        const byId = sources.find(source => String(source.id).trim().toLowerCase() === idNeedle);
        if (byId) return byId;

        const byVariable = sources.find(source => String(source.variable || "").trim().toLowerCase() === idNeedle);
        if (byVariable) return byVariable;

        const normalizedIdNeedle = normalizeWorkProductName(idNeedle.replace(/[-_]+/g, " "));
        const byNormalizedVariable = sources.find(
            source => normalizeWorkProductName(String(source.variable || "").replace(/[-_]+/g, " ")) === normalizedIdNeedle
        );
        if (byNormalizedVariable) return byNormalizedVariable;

        const byElementSuffix = sources.find(source => idNeedle.endsWith(`-${String(source.id).trim().toLowerCase()}`));
        if (byElementSuffix) return byElementSuffix;

        const byVariableSuffix = sources.find(source => idNeedle.endsWith(`-${String(source.variable || "").trim().toLowerCase()}`));
        if (byVariableSuffix) return byVariableSuffix;
    }

    return resolveByName(request.workProductName);
}

function workProductEditDistance(left: string, right: string): number {
    const matrix = Array.from({ length: left.length + 1 }, (_, row) =>
        Array.from({ length: right.length + 1 }, (_, column) => (row === 0 ? column : column === 0 ? row : 0))
    );

    for (let row = 1; row <= left.length; row += 1) {
        for (let column = 1; column <= right.length; column += 1) {
            const substitutionCost = left[row - 1] === right[column - 1] ? 0 : 1;
            matrix[row][column] = Math.min(
                matrix[row - 1][column] + 1,
                matrix[row][column - 1] + 1,
                matrix[row - 1][column - 1] + substitutionCost
            );

            if (
                row > 1 &&
                column > 1 &&
                left[row - 1] === right[column - 2] &&
                left[row - 2] === right[column - 1]
            ) {
                matrix[row][column] = Math.min(matrix[row][column], matrix[row - 2][column - 2] + 1);
            }
        }
    }

    return matrix[left.length][right.length];
}

function contiguousPhrases(value: string): string[] {
    const words = normalizeWorkProductName(value).split(" ").filter(Boolean);
    return words.flatMap((_, start) =>
        words.slice(start).map((__, offset) => words.slice(start, start + offset + 1).join(" "))
    );
}

/** Find the nearest visible work product for confirmation; never use this result to generate automatically. */
export function findClosestWorkProductGenerationSource<T extends SddWorkProductSource & { id: string | number }>(
    sources: readonly T[],
    requestedName: string
): T | undefined {
    const requestedPhrases = contiguousPhrases(requestedName);
    if (requestedPhrases.length === 0) return undefined;

    return sources
        .filter(source => Boolean(normalizeWorkProductName(source.name)))
        .map((source, index) => {
            const sourcePhrases = contiguousPhrases(source.name);
            const comparisons = requestedPhrases.flatMap(requestedPhrase =>
                sourcePhrases.map(sourcePhrase => {
                    const distance = workProductEditDistance(requestedPhrase, sourcePhrase);
                    return {
                        distance,
                        similarity: 1 - distance / Math.max(requestedPhrase.length, sourcePhrase.length, 1)
                    };
                })
            );
            const best = comparisons.sort(
                (left, right) => right.similarity - left.similarity || left.distance - right.distance
            )[0];
            return { source, index, similarity: best?.similarity ?? 0, distance: best?.distance ?? Number.MAX_SAFE_INTEGER };
        })
        .sort(
            (left, right) =>
                right.similarity - left.similarity ||
                left.distance - right.distance ||
                left.index - right.index
        )[0]?.source;
}

export function isFirstDraftReady(source: SddWorkProductSource | undefined): boolean {
    return Boolean(source && (source.hasContent || source.state === "editable" || source.approved));
}

export function requiredFirstDraftsForLevel(level: SddHierarchyLevel): SddRequiredWorkProduct[] {
    return (["specify", "plan", "task_breakdown"] as SddStageKey[]).flatMap(stage =>
        SDD_REQUIRED_WORK_PRODUCTS[stage].filter(workProduct => workProduct.level === level)
    );
}

export function canStartImplementationFromWorkProducts(level: SddHierarchyLevel, sources: readonly SddWorkProductSource[]): boolean {
    const requiredDrafts = requiredFirstDraftsForLevel(level);
    return requiredDrafts.length > 0 && requiredDrafts.every(definition => isFirstDraftReady(findRequiredWorkProductSource(definition, sources)));
}

export const REQUIRED_SPEC_FIELDS = ["title", "problem_statement", "users", "goals", "non_goals", "acceptance_criteria"] as const;

export const STANDARD_SDD_TEMPLATE = `schema_version: "1.0"
title: Feature specification
problem_statement: Describe the user problem this feature solves.
users:
  - Product manager
  - Developer
goals:
  - Define a measurable outcome
non_goals:
  - Capture work that is explicitly out of scope
acceptance_criteria:
  - Given a valid specification, when it is validated, then it is marked compliant
open_questions: []
`;

export const STANDARD_SDD_JSON_TEMPLATE = JSON.stringify(
    {
        schema_version: "1.0",
        title: "Feature specification",
        problem_statement: "Describe the user problem this feature solves.",
        users: ["Product manager", "Developer"],
        goals: ["Define a measurable outcome"],
        non_goals: ["Capture work that is explicitly out of scope"],
        acceptance_criteria: ["Given a valid specification, when it is validated, then it is marked compliant"],
        open_questions: []
    },
    null,
    2
);

function jsonIssues(content: string): SddValidationIssue[] {
    try {
        const parsed = JSON.parse(content) as Record<string, unknown>;
        return REQUIRED_SPEC_FIELDS.filter(field => !(field in parsed)).map(field => ({
            line: 1,
            column: 1,
            message: `Missing required property: ${field}`,
            severity: "error" as const
        }));
    } catch (error) {
        const message = error instanceof Error ? error.message : "Invalid JSON syntax";
        const position = /position\s+(\d+)/i.exec(message)?.[1];
        const offset = position ? Number(position) : 0;
        const before = content.slice(0, offset);
        const line = before.split("\n").length;
        const column = before.length - before.lastIndexOf("\n");
        return [{ line, column, message, severity: "error" }];
    }
}

function yamlIssues(content: string): SddValidationIssue[] {
    const lines = content.split("\n");
    const topLevel = new Set<string>();
    const issues: SddValidationIssue[] = [];

    lines.forEach((line, index) => {
        if (!line.trim() || line.trimStart().startsWith("#")) return;
        const topLevelMatch = /^([A-Za-z_][\w-]*):(?:\s|$)/.exec(line);
        if (topLevelMatch) {
            topLevel.add(topLevelMatch[1]);
            return;
        }
        if (/^\s+/.test(line) || /^\s*-\s+/.test(line)) return;
        issues.push({
            line: index + 1,
            column: 1,
            message: "Expected a YAML property in the form ‘name: value’.",
            severity: "error"
        });
    });

    REQUIRED_SPEC_FIELDS.forEach(field => {
        if (!topLevel.has(field)) {
            issues.push({ line: 1, column: 1, message: `Missing required property: ${field}`, severity: "error" });
        }
    });

    return issues;
}

export function validateSddSpec(content: string, format: SddSpecFormat): SddValidationIssue[] {
    if (!content.trim()) return [{ line: 1, column: 1, message: "Specification cannot be empty.", severity: "error" }];
    return format === "json" ? jsonIssues(content) : yamlIssues(content);
}

export function canStartImplementation(completedDrafts: ReadonlySet<SddStageKey>): boolean {
    return ["specify", "plan", "task_breakdown"].every(stage => completedDrafts.has(stage as SddStageKey));
}

export function sddStorageKey(projectId: string | number, featureId?: string | number | null, storyId?: string | number | null): string {
    return ["feature-studio-sdd", projectId, featureId ?? "product", storyId ?? "none"].join(":");
}
